class BeautyLaunchTheme {
  constructor() {
    this.bindMobileMenu();
    this.bindDesktopNavigation();
    this.bindStickyHeader();
    this.bindLazyVideos();
    this.bindProductGallery();
    this.bindVariantPickers();
    this.bindVariantMinimumQuantities();
    this.bindCartMinimumQuantities();
    this.bindHeroLogoStudio();
    this.bindHomepageScrollStory();
    this.bindProductInquiryIntents();
    this.bindContactSourceFields();
    this.bindLogoCustomizerForms();
    this.focusPostedContactForm();
    this.enhanceCartDesignPreviews();
    this.buildArticleToc();
  }

  bindMobileMenu() {
    const buttons = document.querySelectorAll('[data-mobile-menu-button]');

    buttons.forEach((button) => {
      const targetId = button.getAttribute('aria-controls');
      const drawer = targetId ? document.getElementById(targetId) : null;

      if (!drawer) return;

      const closeDrawer = () => {
        button.setAttribute('aria-expanded', 'false');
        drawer.setAttribute('aria-hidden', 'true');
        drawer.classList.remove('is-open');
      };

      const openDrawer = () => {
        button.setAttribute('aria-expanded', 'true');
        drawer.setAttribute('aria-hidden', 'false');
        drawer.classList.add('is-open');
        const firstLink = drawer.querySelector('a, button, summary');
        if (firstLink) firstLink.focus({ preventScroll: true });
      };

      button.addEventListener('click', () => {
        const isExpanded = button.getAttribute('aria-expanded') === 'true';
        if (isExpanded) {
          closeDrawer();
        } else {
          openDrawer();
        }
      });

      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          closeDrawer();
          button.focus({ preventScroll: true });
        }
      });

      document.addEventListener('click', (event) => {
        const isExpanded = button.getAttribute('aria-expanded') === 'true';
        if (!isExpanded) return;
        if (drawer.contains(event.target) || button.contains(event.target)) return;
        closeDrawer();
      });
    });
  }

  bindDesktopNavigation() {
    const items = document.querySelectorAll('.header-nav__item.has-dropdown');

    items.forEach((item) => {
      const trigger = item.querySelector('.header-nav__link[aria-haspopup="true"]');
      if (!trigger) return;

      item.addEventListener('mouseenter', () => trigger.setAttribute('aria-expanded', 'true'));
      item.addEventListener('mouseleave', () => trigger.setAttribute('aria-expanded', 'false'));
      item.addEventListener('focusin', () => trigger.setAttribute('aria-expanded', 'true'));
      item.addEventListener('focusout', (event) => {
        if (!item.contains(event.relatedTarget)) {
          trigger.setAttribute('aria-expanded', 'false');
        }
      });
    });
  }

  bindStickyHeader() {
    const header = document.querySelector('[data-site-header].site-header--sticky');
    if (!header) return;

    const placeholder = document.createElement('div');
    placeholder.className = 'site-header-placeholder';
    placeholder.hidden = true;
    header.after(placeholder);

    let headerTop = 0;

    const measure = () => {
      const wasFixed = header.classList.contains('is-fixed');
      if (wasFixed) header.classList.remove('is-fixed');
      headerTop = header.getBoundingClientRect().top + window.scrollY;
      placeholder.style.height = `${header.offsetHeight}px`;
      if (wasFixed) header.classList.add('is-fixed');
    };

    const update = () => {
      const shouldFix = window.scrollY > headerTop;
      header.classList.toggle('is-fixed', shouldFix);
      placeholder.hidden = !shouldFix;
    };

    measure();
    update();
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', () => {
      measure();
      update();
    });
  }

  bindLazyVideos() {
    const videos = document.querySelectorAll('video[data-lazy-video]');

    if (!('IntersectionObserver' in window)) {
      videos.forEach((video) => video.load());
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;

        const video = entry.target;
        video.querySelectorAll('source[data-src]').forEach((source) => {
          source.src = source.dataset.src;
          source.removeAttribute('data-src');
        });
        video.load();
        observer.unobserve(video);
      });
    }, { rootMargin: '300px 0px' });

    videos.forEach((video) => observer.observe(video));
  }

  bindProductGallery() {
    const galleries = document.querySelectorAll('[data-private-label-product], .product-inquiry-main');

    galleries.forEach((gallery) => {
      const mainImage = gallery.querySelector('[data-product-gallery-image]');
      const thumbs = gallery.querySelectorAll('[data-product-gallery-thumb]');

      if (!mainImage || !thumbs.length) return;

      const mainFrame = mainImage.closest('[data-product-gallery-main]');
      const preloadImage = (src, srcset) => new Promise((resolve, reject) => {
        const image = new Image();
        image.onload = () => resolve(image);
        image.onerror = reject;
        if (srcset) image.srcset = srcset;
        image.src = src;
      });

      thumbs.forEach((thumb) => {
        thumb.addEventListener('click', async () => {
          const src = thumb.dataset.gallerySrc;
          const srcset = thumb.dataset.gallerySrcset;
          const alt = thumb.dataset.galleryAlt;

          if (!src) return;
          if (thumb.classList.contains('is-active')) return;

          thumbs.forEach((item) => {
            item.classList.remove('is-loading');
            item.setAttribute('aria-current', 'false');
          });
          thumb.classList.add('is-loading');
          mainFrame?.classList.add('is-switching');

          try {
            const loadedImage = await preloadImage(src, srcset);
            if (loadedImage.decode) {
              await loadedImage.decode().catch(() => {});
            }

            mainImage.src = src;
            if (srcset) mainImage.srcset = srcset;
            if (alt) mainImage.alt = alt;

            thumbs.forEach((item) => {
              item.classList.remove('is-active');
              item.setAttribute('aria-current', 'false');
            });

            thumb.classList.add('is-active');
            thumb.setAttribute('aria-current', 'true');
          } finally {
            thumb.classList.remove('is-loading');
            window.requestAnimationFrame(() => {
              mainFrame?.classList.remove('is-switching');
            });
          }
        });
      });
    });
  }

  bindVariantPickers() {
    const pickers = document.querySelectorAll('[data-variant-picker]');

    pickers.forEach((picker) => {
      const select = picker.querySelector('[data-variant-picker-select]');
      const trigger = picker.querySelector('[data-variant-picker-trigger]');
      const label = picker.querySelector('[data-variant-picker-label]');
      const menu = picker.querySelector('[data-variant-picker-menu]');
      const options = picker.querySelectorAll('[data-variant-picker-option]');

      if (!select || !trigger || !label || !menu || !options.length) return;

      const close = () => {
        picker.classList.remove('is-open');
        trigger.setAttribute('aria-expanded', 'false');
      };

      const open = () => {
        picker.classList.add('is-open');
        trigger.setAttribute('aria-expanded', 'true');
        const selected = picker.querySelector('[data-variant-picker-option].is-selected:not(:disabled)') || options[0];
        selected?.focus({ preventScroll: true });
      };

      const setValue = (option) => {
        if (!option || option.disabled) return;

        select.value = option.dataset.value;
        label.textContent = option.textContent.trim().replace(/\s+/g, ' ');
        options.forEach((item) => {
          const isSelected = item === option;
          item.classList.toggle('is-selected', isSelected);
          item.setAttribute('aria-selected', String(isSelected));
        });
        select.dispatchEvent(new Event('change', { bubbles: true }));
        close();
        trigger.focus({ preventScroll: true });
      };

      trigger.addEventListener('click', () => {
        if (picker.classList.contains('is-open')) {
          close();
        } else {
          open();
        }
      });

      options.forEach((option) => {
        option.addEventListener('click', () => setValue(option));
      });

      document.addEventListener('click', (event) => {
        if (!picker.contains(event.target)) close();
      });

      picker.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          close();
          trigger.focus({ preventScroll: true });
        }
      });
    });
  }

  bindVariantMinimumQuantities() {
    const getForms = () => Array.from(document.querySelectorAll('form[action*="/cart/add"]'));
    if (!getForms().length) return;

    const normalizeQuantity = (value, fallback = 1) => {
      const quantity = parseInt(value, 10);
      return Number.isFinite(quantity) && quantity > 0 ? quantity : fallback;
    };

    const getMinimumQuantity = (variantId, form) => {
      const selector = variantId ? `[data-value="${variantId}"], option[value="${variantId}"]` : '[data-variant-picker-option][aria-selected="true"], [data-variant-picker-select] option:checked';
      const source = document.querySelector(selector);
      const sourceQuantity = normalizeQuantity(source?.dataset?.minQuantity, 0);
      if (sourceQuantity > 0) return sourceQuantity;

      const quantityField = form?.querySelector('[data-min-quantity-field], input[name="quantity"]');
      return normalizeQuantity(quantityField?.min || quantityField?.value);
    };

    const syncFormQuantity = (form, variantId) => {
      const minQuantity = getMinimumQuantity(variantId, form);
      const variantInput = form.querySelector('input[name="id"]');
      if (variantId && variantInput) variantInput.value = variantId;

      form.querySelectorAll('[data-min-quantity-field], input[name="quantity"]').forEach((input) => {
        input.min = String(minQuantity);
        input.value = String(minQuantity);
      });

      const note = document.querySelector('[data-min-quantity-note]');
      const noteValue = document.querySelector('[data-min-quantity-value]');
      if (note && noteValue) {
        note.hidden = minQuantity <= 1;
        noteValue.textContent = String(minQuantity);
      }
    };

    const syncAllForms = (variantId) => {
      getForms().forEach((form) => syncFormQuantity(form, variantId));
    };

    const bindForm = (form) => {
      if (!(form instanceof HTMLFormElement)) return;
      if (form.dataset.minimumQuantityBound === 'true') return;

      form.dataset.minimumQuantityBound = 'true';
      const variantInput = form.querySelector('input[name="id"], select[name="id"]');
      syncFormQuantity(form, variantInput?.value);

      form.addEventListener('submit', () => {
        const currentVariantInput = form.querySelector('input[name="id"], select[name="id"]');
        syncFormQuantity(form, currentVariantInput?.value);
      });
    };

    document.querySelectorAll('[data-variant-picker-select], form[action*="/cart/add"] select[name="id"]').forEach((select) => {
      syncAllForms(select.value);
      select.addEventListener('change', () => syncAllForms(select.value));
    });

    document.querySelectorAll('[data-variant-picker-option]').forEach((option) => {
      option.addEventListener('click', () => syncAllForms(option.dataset?.value));
    });

    getForms().forEach(bindForm);

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (!(node instanceof Element)) return;
          if (node.matches('form[action*="/cart/add"]')) bindForm(node);
          node.querySelectorAll('form[action*="/cart/add"]').forEach(bindForm);
        });
      });
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }

  bindCartMinimumQuantities() {
    const inputs = document.querySelectorAll('[data-cart-min-quantity]');
    if (!inputs.length) return;

    const applyMinimum = (input) => {
      const minQuantity = parseInt(input.dataset.cartMinQuantity || input.min || '1', 10);
      const currentQuantity = parseInt(input.value || '0', 10);
      if (!Number.isFinite(minQuantity) || minQuantity <= 1) return false;
      if (Number.isFinite(currentQuantity) && currentQuantity >= minQuantity) return false;

      input.value = String(minQuantity);
      input.classList.add('is-corrected');
      const message = input.closest('.cart-item__actions')?.querySelector('[data-cart-min-quantity-message]');
      if (message) message.classList.add('is-visible');
      return true;
    };

    inputs.forEach((input) => {
      input.addEventListener('change', () => applyMinimum(input));
      applyMinimum(input);
    });

    const form = inputs[0].closest('form');
    form?.addEventListener('submit', () => {
      inputs.forEach(applyMinimum);
    });

    document.querySelector('[data-cart-checkout-link]')?.addEventListener('click', (event) => {
      const corrected = Array.from(inputs).some(applyMinimum);
      if (corrected) {
        event.preventDefault();
        event.stopImmediatePropagation();
        form?.requestSubmit();
      }
    }, true);
  }

  bindHeroLogoStudio() {
    const studios = document.querySelectorAll('[data-hero-logo-studio]');
    if (!studios.length) return;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    studios.forEach((studio) => {
      let pointerProgress = 0;
      let scrollProgress = 0;
      let rafId = null;

      const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

      const schedule = () => {
        if (rafId) return;

        rafId = window.requestAnimationFrame(() => {
          rafId = null;
          studio.style.setProperty('--studio-logo-progress', Math.max(pointerProgress, scrollProgress).toFixed(3));
        });
      };

      const setTilt = (clientX, clientY) => {
        if (reduceMotion) return;

        const rect = studio.getBoundingClientRect();
        const x = ((clientX - rect.left) / rect.width) - 0.5;
        const y = ((clientY - rect.top) / rect.height) - 0.5;
        studio.style.setProperty('--studio-tilt-y', `${clamp(x * 16, -8, 8).toFixed(2)}deg`);
        studio.style.setProperty('--studio-tilt-x', `${clamp(y * -12, -6, 6).toFixed(2)}deg`);
      };

      studio.addEventListener('pointermove', (event) => {
        pointerProgress = 1;
        setTilt(event.clientX, event.clientY);
        schedule();
      });

      studio.addEventListener('pointerleave', () => {
        pointerProgress = 0;
        studio.style.setProperty('--studio-tilt-x', '0deg');
        studio.style.setProperty('--studio-tilt-y', '0deg');
        schedule();
      });

      const updateScrollProgress = () => {
        const rect = studio.getBoundingClientRect();
        const viewport = window.innerHeight || document.documentElement.clientHeight;
        const visible = clamp((viewport - rect.top) / (viewport + rect.height), 0, 1);
        scrollProgress = clamp((visible - 0.2) / 0.55, 0, 1);
        schedule();
      };

      updateScrollProgress();
      window.addEventListener('scroll', updateScrollProgress, { passive: true });
      window.addEventListener('resize', updateScrollProgress);
    });
  }

  bindHomepageScrollStory() {
    const main = document.querySelector('[data-scroll-story]');
    const stage = document.querySelector('[data-scroll-story-stage]');
    if (!main || !stage) return;

    let scenes = [];
    try {
      scenes = JSON.parse(main.dataset.scrollStoryScenes || '[]');
    } catch (error) {
      scenes = [];
    }

    const currentLayer = stage.querySelector('[data-scroll-story-current]');
    const nextLayer = stage.querySelector('[data-scroll-story-next]');
    const logo = stage.querySelector('[data-scroll-story-logo-text]');
    const caption = stage.querySelector('[data-scroll-story-caption]');
    if (!currentLayer || !nextLayer || !scenes.length) return;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let activeIndex = -1;
    let rafId = null;

    const findTarget = (scene) => {
      const suffix = scene.target?.split('--__').pop();
      return document.getElementById(scene.target)
        || (suffix ? document.querySelector(`.main-content > .shopify-section[id$="__${suffix}"]`) : null)
        || (scene.fallbackTarget ? document.getElementById(scene.fallbackTarget)?.closest('.shopify-section') : null);
    };

    const preparedScenes = scenes
      .map((scene) => ({ ...scene, element: findTarget(scene) }))
      .filter((scene) => scene.element && scene.image);

    if (!preparedScenes.length) return;

    const loadedImages = new Set();
    const preloadScene = (index) => {
      const scene = preparedScenes[index];
      if (!scene || loadedImages.has(scene.image)) return;
      loadedImages.add(scene.image);
      const image = new Image();
      image.decoding = 'async';
      image.src = scene.image;
    };

    const applyScene = (index) => {
      if (index === activeIndex || !preparedScenes[index]) return;

      const scene = preparedScenes[index];
      nextLayer.style.backgroundImage = `url("${scene.image}")`;
      stage.classList.add('is-swapping');

      window.setTimeout(() => {
        currentLayer.style.backgroundImage = `url("${scene.image}")`;
        stage.classList.remove('is-swapping');
      }, 520);

      if (caption) caption.textContent = scene.label || '';
      if (logo && main.dataset.scrollStoryLogo) logo.textContent = main.dataset.scrollStoryLogo;
      activeIndex = index;
      preloadScene(index + 1);
    };

    const update = () => {
      rafId = null;

      const viewport = window.innerHeight || document.documentElement.clientHeight;
      const viewportCenter = viewport * 0.52;
      let bestIndex = -1;
      let bestDistance = Infinity;
      let storyOpacity = 0;
      let progress = 0;

      preparedScenes.forEach((scene, index) => {
        const rect = scene.element.getBoundingClientRect();
        const sectionCenter = rect.top + rect.height * 0.5;
        const distance = Math.abs(sectionCenter - viewportCenter);
        const isNear = rect.top < viewport * 0.82 && rect.bottom > viewport * 0.18;

        if (isNear && distance < bestDistance) {
          bestIndex = index;
          bestDistance = distance;
          progress = Math.min(Math.max((viewport - rect.top) / (viewport + rect.height), 0), 1);
          const visibleRatio = Math.min(Math.max((Math.min(rect.bottom, viewport) - Math.max(rect.top, 0)) / Math.min(rect.height, viewport), 0), 1);
          storyOpacity = Math.max(storyOpacity, Math.min(visibleRatio * 1.35, 1));
        }
      });

      if (bestIndex >= 0) {
        applyScene(bestIndex);
      }

      const y = reduceMotion ? 0 : (0.5 - progress) * 56;
      const scale = reduceMotion ? 1 : 1.035 + (progress * 0.035);
      stage.style.setProperty('--scroll-story-opacity', storyOpacity.toFixed(3));
      stage.style.setProperty('--scroll-story-y', `${y.toFixed(2)}px`);
      stage.style.setProperty('--scroll-story-scale', scale.toFixed(4));
    };

    const schedule = () => {
      if (rafId) return;
      rafId = window.requestAnimationFrame(update);
    };

    currentLayer.style.backgroundImage = `url("${preparedScenes[0].image}")`;
    loadedImages.add(preparedScenes[0].image);
    if (caption) caption.textContent = preparedScenes[0].label || '';
    if (logo && main.dataset.scrollStoryLogo) logo.textContent = main.dataset.scrollStoryLogo;

    update();
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);

    const preloadNext = () => preloadScene(1);
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(preloadNext, { timeout: 1600 });
    } else {
      window.setTimeout(preloadNext, 900);
    }
  }

  bindContactSourceFields() {
    const timestampInputs = document.querySelectorAll('form[action*="/contact"] [data-submitted-at]');
    if (!timestampInputs.length) return;

    timestampInputs.forEach((input) => {
      const form = input.closest('form');
      if (!form) return;

      form.addEventListener('submit', () => {
        input.value = new Date().toISOString();
      });
    });
  }

  bindLogoCustomizerForms() {
    const hostForm = document.querySelector('[data-logo-customizer-product-form]');
    if (!hostForm) return;

    const ensureInput = (form, name, value) => {
      if (!value) return;
      let input = form.querySelector(`input[name="${name}"]`);

      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        form.appendChild(input);
      }

      input.value = value;
    };

    const getSelectedVariantId = (preferredVariantId) => {
      if (preferredVariantId) return preferredVariantId;

      const variantSelect = document.querySelector('[data-variant-picker-select]');
      if (variantSelect?.value) return variantSelect.value;

      const selectedOption = document.querySelector('[data-variant-picker-option][aria-selected="true"]');
      if (selectedOption?.dataset?.value) return selectedOption.dataset.value;

      const urlVariantId = new URLSearchParams(window.location.search).get('variant');
      if (urlVariantId && /^\d+$/.test(urlVariantId)) return urlVariantId;

      const hostVariantInput = hostForm.querySelector('input[name="id"], select[name="id"]');
      if (hostVariantInput?.value) return hostVariantInput.value;

      const cartVariantInput = document.querySelector('form[action*="/cart/add"] input[name="id"], form[action*="/cart/add"] select[name="id"]');
      return cartVariantInput?.value || '';
    };

    const syncVariantId = (form, preferredVariantId) => {
      if (!form) return '';

      const variantId = getSelectedVariantId(preferredVariantId);
      if (!variantId) return '';

      const input = form.querySelector('input[name="id"]');
      const select = form.querySelector('select[name="id"]');

      if (input) {
        input.value = variantId;
      } else if (!select) {
        ensureInput(form, 'id', variantId);
      }

      return variantId;
    };

    const getSelectedMinimumQuantity = (preferredVariantId) => {
      const variantId = getSelectedVariantId(preferredVariantId);
      const selector = variantId ? `[data-value="${variantId}"], option[value="${variantId}"]` : '[data-variant-picker-option][aria-selected="true"], [data-variant-picker-select] option:checked';
      const source = document.querySelector(selector);
      const parsedSourceQuantity = parseInt(source?.dataset?.minQuantity || '', 10);
      if (Number.isFinite(parsedSourceQuantity) && parsedSourceQuantity > 0) return parsedSourceQuantity;

      const quantityInput = hostForm.querySelector('[data-min-quantity-field], input[name="quantity"]');
      const parsedFormQuantity = parseInt(quantityInput?.min || quantityInput?.value || '', 10);
      return Number.isFinite(parsedFormQuantity) && parsedFormQuantity > 0 ? parsedFormQuantity : 1;
    };

    const syncQuantity = (form, preferredVariantId) => {
      if (!form) return 1;

      const minQuantity = getSelectedMinimumQuantity(preferredVariantId);
      let quantityInput = form.querySelector('input[name="quantity"]');

      if (!quantityInput) {
        quantityInput = document.createElement('input');
        quantityInput.type = 'hidden';
        quantityInput.name = 'quantity';
        form.appendChild(quantityInput);
      }

      quantityInput.min = String(minQuantity);
      quantityInput.value = String(minQuantity);
      return minQuantity;
    };

    const syncCartPayloadFormData = (body) => {
      const variantId = getSelectedVariantId();
      if (variantId) body.set('id', variantId);
      body.set('quantity', String(getSelectedMinimumQuantity(variantId)));
      return body;
    };

    const syncCartPayloadUrlSearchParams = (body) => {
      const variantId = getSelectedVariantId();
      if (variantId) body.set('id', variantId);
      body.set('quantity', String(getSelectedMinimumQuantity(variantId)));
      return body;
    };

    const syncCartPayloadJson = (body) => {
      if (!body || typeof body !== 'object' || Array.isArray(body)) return body;
      const variantId = getSelectedVariantId(body.id || body.items?.[0]?.id);
      const quantity = getSelectedMinimumQuantity(variantId);

      if (Array.isArray(body.items) && body.items.length > 0) {
        body.items[0] = { ...body.items[0], id: variantId || body.items[0].id, quantity };
        return body;
      }

      return { ...body, id: variantId || body.id, quantity };
    };

    const syncCartForms = (preferredVariantId) => {
      document.querySelectorAll('form[action*="/cart/add"]').forEach((form) => {
        syncVariantId(form, preferredVariantId);
        syncQuantity(form, preferredVariantId);
      });
    };

    if (!syncVariantId(hostForm)) return;

    document.querySelectorAll('[data-variant-picker-select], form[action*="/cart/add"] select[name="id"]').forEach((select) => {
      select.addEventListener('change', () => syncCartForms(select.value));
    });

    document.querySelectorAll('[data-variant-picker-option]').forEach((option) => {
      option.addEventListener('click', () => syncCartForms(option.dataset?.value));
    });

    const customizerUploadUrls = {};
    const nativeFormSubmit = HTMLFormElement.prototype.submit;

    const saveCustomizerUploadUrls = (urls) => {
      if (!urls || typeof urls !== 'object') return;
      ['front_preview', 'box_preview', 'front_raw', 'box_raw'].forEach((key) => {
        if (urls[key]) customizerUploadUrls[key] = urls[key];
      });
    };

    const applyCustomizerUploadUrls = (form) => {
      if (!form) return;

      if (customizerUploadUrls.front_preview) {
        ensureInput(form, 'properties[Product Preview]', customizerUploadUrls.front_preview);
      }

      if (customizerUploadUrls.box_preview) {
        ensureInput(form, 'properties[Packaging Preview]', customizerUploadUrls.box_preview);
      }

      if (customizerUploadUrls.front_raw && customizerUploadUrls.box_raw && customizerUploadUrls.front_raw === customizerUploadUrls.box_raw) {
        ensureInput(form, 'properties[_PrintFile]', customizerUploadUrls.front_raw);
      } else {
        if (customizerUploadUrls.front_raw) ensureInput(form, 'properties[_ProductPrintFile]', customizerUploadUrls.front_raw);
        if (customizerUploadUrls.box_raw) ensureInput(form, 'properties[_PackagingPrintFile]', customizerUploadUrls.box_raw);
      }
    };

    window.__beautyLaunchApplyLogoCustomizerProperties = applyCustomizerUploadUrls;

    const collectPreviewLinks = () => {
      const links = Array.from(hostForm.querySelectorAll('a[href]'));
      const previewLinks = {};

      links.forEach((link) => {
        const text = link.textContent.trim().replace(/\s+/g, ' ').toLowerCase();
        const href = link.href;
        if (!href) return;

        if (text.includes('product preview')) {
          previewLinks.product = href;
        }

        if (text.includes('packaging preview') || text.includes('packagingpreview')) {
          previewLinks.packaging = href;
        }
      });

      return previewLinks;
    };

    const addCustomizerProperties = (form) => {
      syncQuantity(form);
      applyCustomizerUploadUrls(form);
      const previewLinks = collectPreviewLinks();
      if (previewLinks.product && !form.querySelector('input[name="properties[Product Preview]"]')) {
        ensureInput(form, 'properties[Product Preview]', previewLinks.product);
      }
      if (previewLinks.packaging && !form.querySelector('input[name="properties[Packaging Preview]"]')) {
        ensureInput(form, 'properties[Packaging Preview]', previewLinks.packaging);
      }
    };

    const animateAddToCart = () => {
      const cartIcon = document.querySelector('.header-icon-link--cart');
      const source = hostForm.querySelector('img, canvas, svg') || hostForm.querySelector('button[type="submit"], button, input[type="submit"]');
      if (!cartIcon || !source) return Promise.resolve();

      const sourceRect = source.getBoundingClientRect();
      const cartRect = cartIcon.getBoundingClientRect();
      const flyer = document.createElement('div');
      flyer.className = 'cart-flyout-animation';
      flyer.style.left = `${sourceRect.left + sourceRect.width / 2}px`;
      flyer.style.top = `${sourceRect.top + sourceRect.height / 2}px`;

      const image = source.matches('img') ? source.cloneNode(true) : null;
      if (image) {
        image.removeAttribute('id');
        flyer.appendChild(image);
      }

      document.body.appendChild(flyer);
      const deltaX = cartRect.left + cartRect.width / 2 - (sourceRect.left + sourceRect.width / 2);
      const deltaY = cartRect.top + cartRect.height / 2 - (sourceRect.top + sourceRect.height / 2);

      return new Promise((resolve) => {
        requestAnimationFrame(() => {
          flyer.style.setProperty('--cart-flyout-x', `${deltaX}px`);
          flyer.style.setProperty('--cart-flyout-y', `${deltaY}px`);
          flyer.classList.add('is-flying');
          cartIcon.classList.add('is-cart-target');
        });

        window.setTimeout(() => {
          flyer.remove();
          cartIcon.classList.remove('is-cart-target');
          resolve();
        }, 720);
      });
    };

    const submitWithAnimation = async (form, event) => {
      if (form.dataset.logoCustomizerSubmitting === 'true') return;
      const action = form.getAttribute('action') || '';
      if (!action.includes('/cart/add')) return;

      syncVariantId(form);
      syncQuantity(form);
      addCustomizerProperties(form);

      if (!window.fetch || !window.FormData) return;

      event.preventDefault();
      form.dataset.logoCustomizerSubmitting = 'true';

      try {
        const addUrl = action.includes('/cart/add.js') ? action : action.replace('/cart/add', '/cart/add.js');
        const formData = new FormData(form);
        syncCartPayloadFormData(formData);

        const response = await fetch(addUrl, {
          method: 'POST',
          body: formData,
          headers: { Accept: 'application/json' }
        });

        if (!response.ok) throw new Error('Cart add failed');
        await animateAddToCart();
        window.location.href = hostForm.dataset.cartUrl || '/cart';
      } catch (error) {
        form.dataset.logoCustomizerSubmitting = 'false';
        nativeFormSubmit.call(form);
      }
    };

    const patchFetchForUploadUrls = () => {
      if (!window.fetch || window.__beautyLaunchLogoCustomizerFetchPatched) return;

      const originalFetch = window.fetch.bind(window);
      window.__beautyLaunchLogoCustomizerFetchPatched = true;

      window.fetch = async (...args) => {
        const requestUrl = typeof args[0] === 'string' ? args[0] : args[0]?.url;

        if (requestUrl && requestUrl.includes('/cart/add')) {
          const init = args[1] || {};

          if (init.body instanceof FormData) {
            syncCartPayloadFormData(init.body);
          } else if (init.body instanceof URLSearchParams) {
            syncCartPayloadUrlSearchParams(init.body);
          } else if (typeof init.body === 'string') {
            try {
              const payload = JSON.parse(init.body);
              init.body = JSON.stringify(syncCartPayloadJson(payload));
              args[1] = init;
            } catch (error) {
              const params = new URLSearchParams(init.body);
              if (params.has('id') || params.has('quantity')) {
                syncCartPayloadUrlSearchParams(params);
                init.body = params.toString();
                args[1] = init;
              }
            }
          }
        }

        const response = await originalFetch(...args);

        if (requestUrl && requestUrl.includes('/apps/coosei-uv/upload')) {
          try {
            const data = await response.clone().json();
            if (data && data.success && data.urls) {
              saveCustomizerUploadUrls(data.urls);
              document.querySelectorAll('form[action*="/cart/add"]').forEach((form) => {
                applyCustomizerUploadUrls(form);
              });
            }
          } catch (error) {
            // Keep the app upload flow untouched if the response is not JSON.
          }
        }

        return response;
      };
    };

    const patchNativeFormSubmit = () => {
      if (window.__beautyLaunchLogoCustomizerSubmitPatched) return;

      window.__beautyLaunchLogoCustomizerSubmitPatched = true;

      HTMLFormElement.prototype.submit = function patchedSubmit() {
        const action = this.getAttribute('action') || '';

        if (action.includes('/cart/add')) {
          syncVariantId(this);
          syncQuantity(this);
          addCustomizerProperties(this);

          if (this.dataset.logoCustomizerBound === 'true' && this.dataset.logoCustomizerSubmitting !== 'true') {
            submitWithAnimation(this, { preventDefault() {} });
            return undefined;
          }
        }

        return nativeFormSubmit.call(this);
      };
    };

    const bindCartForm = (form) => {
      if (!(form instanceof HTMLFormElement)) return;
      const action = form.getAttribute('action') || '';
      if (!action.includes('/cart/add')) return;
      if (!form.querySelector('input[name="id"], select[name="id"]')) {
        ensureInput(form, 'id', getSelectedVariantId());
      }
      syncVariantId(form);
      syncQuantity(form);

      if (form.dataset.logoCustomizerBound === 'true') return;
      form.dataset.logoCustomizerBound = 'true';
      form.addEventListener('submit', (event) => submitWithAnimation(form, event));
    };

    hostForm.addEventListener('click', (event) => {
      const trigger = event.target.closest('button, a, input[type="submit"], input[type="button"]');
      if (!trigger) return;

      const triggerText = trigger.textContent || trigger.value || '';
      const normalizedText = triggerText.replace(/\s+/g, ' ').toLowerCase();
      if (!normalizedText.includes('confirm') || !normalizedText.includes('add to cart')) return;

      addCustomizerProperties(hostForm);
    }, true);

    patchFetchForUploadUrls();
    patchNativeFormSubmit();

    document.querySelectorAll('form').forEach(bindCartForm);

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (!(node instanceof Element)) return;
          if (node.matches('form')) bindCartForm(node);
          node.querySelectorAll('form').forEach(bindCartForm);
        });
      });
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }

  bindProductInquiryIntents() {
    document.querySelectorAll('[data-inquiry-focus]').forEach((trigger) => {
      trigger.addEventListener('click', () => {
        const target = trigger.getAttribute('data-inquiry-focus');
        if (!target) return;

        window.setTimeout(() => {
          const field = document.querySelector(`[data-inquiry-field="${target}"]`);
          if (field) field.focus({ preventScroll: true });
        }, 450);
      });
    });
  }

  focusPostedContactForm() {
    const params = new URLSearchParams(window.location.search);
    if (!params.has('contact_posted')) return;

    const inquiry = document.getElementById('product-inquiry');
    const success = inquiry?.querySelector('[data-form-success]');
    if (!inquiry || !success) return;

    window.requestAnimationFrame(() => {
      inquiry.scrollIntoView({ behavior: 'smooth', block: 'start' });
      success.focus({ preventScroll: true });
    });
  }

  enhanceCartDesignPreviews() {
    if (!document.body.classList.contains('template-cart')) return;

    const cartItems = Array.from(document.querySelectorAll('[data-cart-item-key]'));
    if (!cartItems.length || !window.fetch) return;

    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then((response) => (response.ok ? response.json() : null))
      .then((cart) => {
        if (!cart || !Array.isArray(cart.items)) return;

        if (window.location.search.includes('debug_customizer=1')) {
          console.info('[BeautyLaunch] cart item properties', cart.items.map((item) => ({ key: item.key, properties: item.properties })));

          if (!cart.items.some((item) => item.properties && (item.properties['Product Preview'] || item.properties['Packaging Preview']))) {
            const firstDetails = document.querySelector('.cart-item__details');
            if (firstDetails && !document.querySelector('.cart-design-debug')) {
              const debug = document.createElement('div');
              debug.className = 'cart-design-debug';
              debug.textContent = 'Debug: /cart.js does not contain Product Preview or Packaging Preview properties for current cart items.';
              firstDetails.prepend(debug);
            }
          }
        }

        cart.items.forEach((item) => {
          const properties = item.properties || {};
          const productPreview = properties['Product Preview'];
          const packagingPreview = properties['Packaging Preview'];

          if (!productPreview && !packagingPreview) return;

          const escapedKey = window.CSS && CSS.escape ? CSS.escape(item.key) : item.key.replace(/"/g, '\\"');
          const cartItem = document.querySelector(`[data-cart-item-key="${escapedKey}"]`);
          const details = cartItem?.querySelector('.cart-item__details');
          if (!details || details.querySelector('.cart-design-assets')) return;

          const assets = document.createElement('div');
          assets.className = 'cart-design-assets';
          assets.setAttribute('aria-label', 'Logo customization design previews');

          const header = document.createElement('div');
          header.className = 'cart-design-assets__header';

          const title = document.createElement('p');
          title.className = 'cart-design-assets__title';
          title.textContent = 'Design previews';

          const hint = document.createElement('span');
          hint.textContent = 'Click thumbnails to open full-size artwork';

          header.append(title, hint);

          const grid = document.createElement('div');
          grid.className = 'cart-design-assets__grid';

          const addPreview = (label, url) => {
            if (!url) return;

            const link = document.createElement('a');
            link.className = 'cart-design-asset';
            link.href = url;
            link.target = '_blank';
            link.rel = 'noopener';
            link.setAttribute('aria-label', `Open ${label} image`);

            const text = document.createElement('span');
            text.textContent = label;

            const image = document.createElement('img');
            image.src = url;
            image.alt = label;
            image.loading = 'lazy';
            image.width = 240;
            image.height = 180;

            link.append(text, image);
            grid.appendChild(link);
          };

          addPreview('Product preview', productPreview);
          addPreview('Packaging preview', packagingPreview);
          assets.append(header, grid);

          const actions = details.querySelector('.cart-item__actions');
          details.insertBefore(assets, actions || null);
        });
      })
      .catch(() => {});
  }

  buildArticleToc() {
    const content = document.querySelector('[data-article-content]');
    const toc = document.querySelector('[data-article-toc]');
    const tocList = document.querySelector('[data-article-toc-list]');

    if (!content || !toc || !tocList) return;

    const headings = Array.from(content.querySelectorAll('h2, h3')).filter((heading) => heading.textContent.trim().length > 0);

    if (!headings.length) {
      toc.hidden = true;
      return;
    }

    headings.forEach((heading, index) => {
      if (!heading.id) {
        heading.id = this.slugify(`${heading.textContent}-${index + 1}`);
      }

      const link = document.createElement('a');
      link.href = `#${heading.id}`;
      link.textContent = heading.textContent;
      link.className = heading.tagName.toLowerCase() === 'h3' ? 'article-toc__link article-toc__link--child' : 'article-toc__link';
      tocList.appendChild(link);
    });
  }

  slugify(value) {
    return value
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9\u4e00-\u9fa5]+/g, '-')
      .replace(/^-+|-+$/g, '') || `section-${Date.now()}`;
  }
}

window.addEventListener('DOMContentLoaded', () => {
  new BeautyLaunchTheme();
});
